import { DirectExecutionType, createDirectExecution } from '@funkit/api-base'
import { LIGHTER_CHAIN_ID } from '@funkit/chains'
import { type Address, pad, parseUnits, toHex } from 'viem'
import { mainnet } from 'viem/chains'

import type { FunLogger } from '../../src/utils/funLogger'
import { randomUuid } from '../../src/utils/randomUuid'
import { LIGHTER_SECURE_ASSETS_BY_INDEX, truncateToDecimals } from './lighter'

/**
 * Records a Lighter Secure withdrawal as an `EXTERNALLY_FULFILLED` direct
 * execution — attribution only, since Fun is not in the execution path.
 * - The backend marks these rows terminal without polling for L1 settlement.
 * - Never rejects: tracking must not fail a withdrawal that already happened,
 *   and callers fire-and-forget it without a `.catch`.
 */
export async function trackLighterSecureWithdrawal({
  apiKey,
  logger,
  userId,
  txHash,
  recipientAddr,
  assetIndex,
  mainnetTokenAddress,
  amountTokenUnits,
  withdrawalUSD,
}: {
  apiKey: string
  logger: FunLogger
  userId: string
  txHash: string
  recipientAddr: Address
  /**
   * Lighter L2 asset index. Used as `fromTokenAddress`
   * (`pad(toHex(assetIndex))`) and to resolve symbol + mainnet decimals from
   * {@link LIGHTER_SECURE_ASSETS_BY_INDEX}.
   */
  assetIndex: number
  /** Mainnet ERC-20 address the L2 asset bridges to (resolved by the modal). */
  mainnetTokenAddress: Address
  /** User-input amount in token units (e.g. "1.5" for 1.5 ETH). */
  amountTokenUnits: string
  /**
   * USD value of the withdrawal as computed by the modal (token amount × live
   * spot price). Avoids a separate spot-price round-trip from this helper.
   */
  withdrawalUSD: string
}): Promise<void> {
  const assetMeta = LIGHTER_SECURE_ASSETS_BY_INDEX[assetIndex]
  if (!assetMeta) {
    // The withdrawal itself succeeded — a missing mapping costs attribution,
    // not funds, so this is recovered rather than alertable.
    logger.warn('lighter:withdrawal:secure:tracking:unknownAssetIndex', {
      assetIndex,
      txHash,
    })
    return
  }
  const { symbol, decimals } = assetMeta

  const estTotalUsd = Number(withdrawalUSD)

  // Lighter L2 assets aren't ERC-20s, so the address slot carries the index
  // (3 → 0x00…03).
  const fromTokenAddress = pad(toHex(assetIndex), { size: 20 }) as Address

  try {
    // Inside the try: `parseUnits` throws on a malformed amount, and callers
    // detach this promise — an escape here is an unhandled rejection after the
    // withdrawal has already landed.
    const baseUnitAmount = parseUnits(
      truncateToDecimals(amountTokenUnits, decimals),
      decimals,
    ).toString()
    await createDirectExecution({
      apiKey,
      logger,
      // Lighter L2 hashes are its own format; the backend stores the literal
      // string and we never read it as an EVM hash.
      txHash: txHash as `0x${string}`,
      type: DirectExecutionType.EXTERNALLY_FULFILLED,
      userId,
      recipientAddr,
      fromChainId: LIGHTER_CHAIN_ID.toString(),
      fromTokenAddress,
      toChainId: mainnet.id.toString(),
      toTokenAddress: mainnetTokenAddress,
      fromAmountBaseUnit: baseUnitAmount,
      toAmountBaseUnit: baseUnitAmount,
      estTotalUsd,
      // Secure locks the payout to the connected wallet, which is also the L1
      // owner of the Lighter account the funds left.
      sourceOfFund: `balance|lighter-secure|${recipientAddr}`,
      clientMetadata: {
        id: randomUuid(),
        startTimestampMs: Date.now(),
        finalDollarValue: estTotalUsd,
        latestQuote: null,
        depositAddress: null,
        initSettings: {
          config: {
            targetAsset: mainnetTokenAddress,
            targetChain: mainnet.id.toString(),
            targetAssetTicker: symbol,
          },
        },
        selectedSourceAssetInfo: {
          address: fromTokenAddress,
          symbol,
          chainId: LIGHTER_CHAIN_ID.toString(),
          iconSrc: null,
        },
        selectedPaymentMethodInfo: {
          paymentMethod: 'LIGHTER_SECURE_WITHDRAWAL',
          title: '',
          description: '',
        },
        isWithdrawal: true,
      },
    })
  } catch (error) {
    logger.warn('lighter:withdrawal:secure:trackingFailed', {
      error: error instanceof Error ? error.message : String(error),
      txHash,
      assetIndex,
      amountTokenUnits,
    })
  }
}
